import { useEffect, useState, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { MdKeyboardArrowDown } from 'react-icons/md';
import {
  nigerianStates,
  marketTabConfig,
  byProductsCardData,
  feedFodderCardCategories,
} from './marketMockData';
import { marketPricesApi } from '../../api/marketPricesApi';
import { marketsApi } from '../../api/marketsApi';

const fmt  = n => Number(n).toLocaleString('en-NG');
const sign = n => (n >= 0 ? '+' : '');

function formatUnitLabel(unit) {
  if (!unit) return '';
  return unit.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
}

// Backend commodity_form codes for "live" animals aren't confirmed to a single
// value — match anything that mentions "live" rather than a hardcoded literal,
// so real records aren't silently excluded by a wrong guess.
function isLiveForm(commodityForm) {
  return /live/i.test(String(commodityForm ?? ''));
}

// ── Shared UI ─────────────────────────────────────────────────────────────────
function StateDropdown({ value, onChange }) {
  return (
    <div className="relative">
      <select
        value={value}
        onChange={e => onChange(e.target.value)}
        className="appearance-none pl-3 pr-8 py-1.5 text-[12px] font-semibold text-gray-700 bg-white border border-gray-200 rounded-lg focus:outline-none focus:border-green-400 cursor-pointer"
      >
        {nigerianStates.map(s => <option key={s}>{s}</option>)}
      </select>
      <MdKeyboardArrowDown className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" size={14} />
    </div>
  );
}

function ViewMoreBtn({ onClick }) {
  return (
    <button
      onClick={onClick}
      className="shrink-0 flex items-center gap-1 text-[11px] font-semibold text-green-700 bg-green-50 border border-green-200 rounded-lg px-2.5 py-1 hover:bg-green-100 transition-colors whitespace-nowrap"
    >
      View More ↗
    </button>
  );
}

function EmptyState({ state }) {
  return (
    <p className="py-16 text-center text-[13px] text-gray-400">
      No data available{state !== 'National' ? ` for ${state}` : ''}
    </p>
  );
}

// ── Pricing Card (Live Animals + Processed Livestock) ─────────────────────────
function PricingCard({ commodity, unit, currentPrice, previousPrice, highestPrice, lowestPrice, dataSource, onViewMore }) {
  const change = currentPrice - previousPrice;
  const isUp   = change >= 0;
  return (
    <div className="min-w-[248px] flex-shrink-0 bg-gray-50 rounded-2xl p-5 flex flex-col border border-gray-100">
      <div className="flex items-start justify-between gap-2 mb-3">
        <div>
          <p className="text-[12px] font-bold text-gray-900 uppercase tracking-wide leading-tight">{commodity}</p>
          <p className="text-[10px] text-gray-400 mt-0.5">{unit}</p>
        </div>
        <ViewMoreBtn onClick={onViewMore} />
      </div>

      <p className="text-[10px] text-gray-400 mb-0.5">Average Current Price (₦)</p>
      <p className="text-[24px] font-bold text-green-600 mb-3">₦{fmt(currentPrice)}</p>

      {[
        { label: 'Previous Price (₦)', value: `₦${fmt(previousPrice)}`,                     cls: 'text-gray-700 font-semibold' },
        { label: 'Change',             value: `${sign(change)}${fmt(change)}`,               cls: isUp ? 'text-green-600 font-bold' : 'text-red-500 font-bold' },
        { label: 'Lowest Price (₦)',   value: lowestPrice  ? `₦${fmt(lowestPrice)}`  : '—', cls: 'text-red-500 font-semibold' },
        { label: 'Highest Price (₦)',  value: highestPrice ? `₦${fmt(highestPrice)}` : '—', cls: 'text-green-600 font-semibold' },
        { label: 'Data Source',        value: dataSource || 'Field Survey',                  cls: 'font-bold text-gray-800' },
      ].map(row => (
        <div key={row.label} className="flex items-center justify-between py-1.5 border-b border-gray-100 last:border-0">
          <span className="text-[10px] text-gray-400">{row.label}</span>
          <span className={`text-[11px] ${row.cls}`}>{row.value}</span>
        </div>
      ))}
    </div>
  );
}

// ── Markets Reporting Card ────────────────────────────────────────────────────
function MarketsReportingCard({ total, highest, lowest, onViewMore }) {
  return (
    <div className="min-w-[248px] flex-shrink-0 bg-gray-50 rounded-2xl p-5 flex flex-col border border-gray-100">
      <div className="flex items-start justify-between gap-2 mb-1">
        <p className="text-[12px] font-bold text-gray-900">Markets Reporting</p>
        <ViewMoreBtn onClick={onViewMore} />
      </div>
      <p className="text-[32px] font-bold text-green-600 mb-3">{total.toLocaleString()}</p>

      <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wide mb-1">Highest Price Markets</p>
      {highest.map((m, i) => (
        <div key={i} className="flex items-center justify-between py-1.5 border-b border-gray-100">
          <div>
            <p className="text-[11px] font-semibold text-gray-800 leading-tight">{m.name}</p>
            <p className="text-[10px] text-gray-400">{m.state}</p>
          </div>
          <span className="text-[11px] font-bold text-green-600">₦{fmt(m.price)}</span>
        </div>
      ))}

      <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wide mt-3 mb-1">Lowest Price Markets</p>
      {lowest.map((m, i) => (
        <div key={i} className={`flex items-center justify-between py-1.5 ${i < lowest.length - 1 ? 'border-b border-gray-100' : ''}`}>
          <div>
            <p className="text-[11px] font-semibold text-gray-800 leading-tight">{m.name}</p>
            <p className="text-[10px] text-gray-400">{m.state}</p>
          </div>
          <span className="text-[11px] font-bold text-gray-500">₦{fmt(m.price)}</span>
        </div>
      ))}
    </div>
  );
}

// ── By-Product Card ───────────────────────────────────────────────────────────
function ByProductCard({ commodity, unit, useCase, marketPriceContext, currentCommodityValue, onViewMore }) {
  return (
    <div className="bg-gray-50 rounded-2xl p-5 border border-gray-100">
      <div className="flex items-start justify-between gap-2 mb-4">
        <div>
          <p className="text-[15px] font-bold text-gray-400 leading-tight">{commodity}</p>
          <p className="text-[11px] text-gray-400 mt-0.5">{unit}</p>
        </div>
        <ViewMoreBtn onClick={onViewMore} />
      </div>

      <div className="space-y-3">
        <div>
          <p className="text-[11px] text-gray-500 mb-0.5">Use Case</p>
          <p className="text-[12px] font-semibold text-gray-900 leading-relaxed">{useCase}</p>
        </div>
        <div className="pt-1 border-t border-gray-200">
          <p className="text-[11px] text-gray-500 mb-0.5">Market Price Context</p>
          <p className="text-[11px] text-gray-500 leading-relaxed">{marketPriceContext}</p>
        </div>
        <div className="pt-1">
          <p className="text-[11px] text-gray-500 mb-0.5">Current Commodity Value</p>
          <p className="text-[12px] font-semibold text-gray-900">{currentCommodityValue}</p>
        </div>
      </div>
    </div>
  );
}

// ── Feed & Fodder Category Card ───────────────────────────────────────────────
function FodderCategoryCard({ category, description, unit, items, onViewMore }) {
  return (
    <div className="bg-gray-50 rounded-2xl p-5 border border-gray-100 flex flex-col">
      <div className="flex items-start justify-between gap-2 mb-3">
        <div>
          <p className="text-[12px] font-bold text-gray-900 leading-tight">{category}</p>
          {description && <p className="text-[10px] text-gray-400 mt-0.5">{description}</p>}
        </div>
        <ViewMoreBtn onClick={onViewMore} />
      </div>
      <p className="text-[10px] text-green-600 font-medium mb-2">{unit}</p>

      <div className="divide-y divide-gray-100 flex-1">
        {items.map((item, i) => (
          <div key={i} className="flex items-center justify-between py-2">
            <span className="text-[11px] text-gray-600 truncate mr-2">{item.name}</span>
            <span className="text-[11px] font-semibold text-gray-800 shrink-0">
              ₦{item.avgPrice.toLocaleString('en-NG')} {unit.replace('Per ', '/ ')}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Main MarketSection ────────────────────────────────────────────────────────
export default function MarketSection() {
  const navigate = useNavigate();

  const [selectedState, setSelectedState] = useState('National');
  const [mainTab,       setMainTab]       = useState(0);
  const [subTab,        setSubTab]        = useState(0);
  const [priceSummary,  setPriceSummary]  = useState(null);
  const [summaryLoading, setSummaryLoading] = useState(false);
  const [marketRegistryData, setMarketRegistryData] = useState([]);

  const tabCfg  = marketTabConfig[mainTab];
  const subTabs = tabCfg.subTabs;
  const tabId   = tabCfg.id;
  const subId   = subTabs.length ? (subTabs[subTab]?.id ?? 'default') : 'default';

  const switchMain = useCallback(i => { setMainTab(i); setSubTab(0); }, []);

  // ── Livestock Production: real /v1/market-prices/summary aggregate ──────────
  // Fetched unfiltered (no commodity_form) — Live vs Processed is split
  // client-side from whatever form code the backend actually returns, since
  // that's the only value guaranteed to match records that were created.
  useEffect(() => {
    if (tabId !== 'livestock-production') return undefined;

    let active = true;
    setSummaryLoading(true);

    marketPricesApi.summary({
      state: selectedState === 'National' ? undefined : selectedState,
      // Backend defaults to a rolling 30-day window, which silently drops
      // anything older — widen it so the card reflects real submitted data,
      // not just prices recorded in the last month.
      window_days: 365,
    })
      .then((summary) => {
        if (active) setPriceSummary(summary);
      })
      .catch(() => {
        if (active) setPriceSummary({ commodities: [], marketsReporting: { total: 0, highest: [], lowest: [] } });
      })
      .finally(() => {
        if (active) setSummaryLoading(false);
      });

    return () => {
      active = false;
    };
  }, [tabId, selectedState]);

  // ── Markets Reporting "total" — always the Market Registry count for the
  // selected state, independent of the price-summary window/filters ─────────
  useEffect(() => {
    let active = true;

    marketsApi.listAll({
      per_page: 500,
      state: selectedState === 'National' ? undefined : selectedState,
    })
      .then(({ items }) => {
        if (active) setMarketRegistryData(items);
      })
      .catch(() => {
        if (active) setMarketRegistryData([]);
      });

    return () => {
      active = false;
    };
  }, [selectedState]);

  const goToMarketPrices = useCallback((ctx = {}) => {
    navigate('/markets-trade/market-prices', {
      state: { fromMarket: true, selectedState, ...ctx },
    });
  }, [navigate, selectedState]);

  const goToMarketRegistry = useCallback(() => {
    navigate('/markets-trade/market-trade');
  }, [navigate]);

  const productionCards = useMemo(() => {
    if (tabId !== 'livestock-production' || !priceSummary) return [];
    const commodities = subId === 'live-animals'
      ? priceSummary.commodities.filter(c => isLiveForm(c.commodityForm))
      : priceSummary.commodities.filter(c => !isLiveForm(c.commodityForm));

    return commodities.map(c => ({
      commodity: c.speciesName,
      species: c.speciesCode,
      unit: formatUnitLabel(c.priceUnit),
      currentPrice: c.currentPrice ?? 0,
      previousPrice: c.previousPrice ?? c.currentPrice ?? 0,
      highestPrice: c.highestPrice,
      lowestPrice: c.lowestPrice,
      dataSource: c.dataSource,
    }));
  }, [tabId, subId, priceSummary]);

  const marketsData = useMemo(() => {
    if (tabId !== 'livestock-production' || subId !== 'live-animals' || !priceSummary) return null;
    return {
      total: marketRegistryData.length,
      highest: priceSummary.marketsReporting.highest,
      lowest: priceSummary.marketsReporting.lowest,
    };
  }, [tabId, subId, priceSummary, marketRegistryData]);

  // ── By-Products: static card data by sub-tab ──────────────────────────────
  const byProductCards = useMemo(() => {
    if (tabId !== 'livestock-byproducts') return [];
    return byProductsCardData[subId] ?? [];
  }, [tabId, subId]);

  return (
    <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-6">

      {/* ── Header ── */}
      <div className="flex items-start justify-between gap-4 mb-5">
        <div>
          <h2 className="text-[17px] font-bold text-gray-900">Markets Pricing Summary</h2>
          <p className="text-[12px] text-gray-400 mt-0.5">
            Livestock market data, pricing and trade across states
          </p>
        </div>
        <StateDropdown value={selectedState} onChange={setSelectedState} />
      </div>

      {/* ── Main tabs ── */}
      <div className="flex items-center gap-8 border-b border-gray-100 mb-4">
        {marketTabConfig.map((tab, i) => (
          <button
            key={tab.id}
            onClick={() => switchMain(i)}
            className={`pb-3 text-[13px] font-semibold whitespace-nowrap border-b-2 -mb-px transition-all ${
              mainTab === i
                ? 'text-green-600 border-green-600'
                : 'text-gray-500 border-transparent hover:text-gray-800'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* ── Sub-tabs ── */}
      {subTabs.length > 0 && (
        <div className="flex items-center gap-2 mb-5 flex-wrap">
          {subTabs.map((st, i) => (
            <button
              key={st.id}
              onClick={() => setSubTab(i)}
              className={`px-4 py-1.5 rounded-full text-[12px] font-semibold border transition-all ${
                subTab === i
                  ? 'bg-green-100 text-green-700 border-green-200'
                  : 'bg-white text-gray-600 border-gray-200 hover:border-gray-300 hover:text-gray-800'
              }`}
            >
              {st.label}
            </button>
          ))}
        </div>
      )}

      {/* ── Livestock Production — horizontal scroll card row ── */}
      {tabId === 'livestock-production' && (
        summaryLoading
          ? <p className="py-16 text-center text-[13px] text-gray-400">Loading market prices…</p>
          : productionCards.length === 0
          ? <EmptyState state={selectedState} />
          : (
            <div className="flex gap-4 overflow-x-auto pb-2 -mx-1 px-1">
              {productionCards.map(card => (
                <PricingCard
                  key={card.commodity}
                  {...card}
                  onViewMore={() => goToMarketPrices({ species: card.species, commodity: card.commodity })}
                />
              ))}
              {marketsData && (
                <MarketsReportingCard
                  total={marketsData.total}
                  highest={marketsData.highest}
                  lowest={marketsData.lowest}
                  onViewMore={goToMarketRegistry}
                />
              )}
            </div>
          )
      )}

      {/* ── Livestock By-Products — full grid, no horizontal scroll ── */}
      {tabId === 'livestock-byproducts' && (
        byProductCards.length === 0
          ? <EmptyState state={selectedState} />
          : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4">
              {byProductCards.map(card => (
                <ByProductCard
                  key={card.id}
                  {...card}
                  onViewMore={() => goToMarketPrices({ commodity: card.commodity })}
                />
              ))}
            </div>
          )
      )}

      {/* ── Feed & Fodder — responsive grid ── */}
      {tabId === 'feed-fodder' && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {feedFodderCardCategories.map(cat => (
            <FodderCategoryCard
              key={cat.id}
              {...cat}
              onViewMore={() => goToMarketPrices({ category: cat.category })}
            />
          ))}
        </div>
      )}
    </div>
  );
}
